// The decisions behind reading a practice line aloud, kept away from the DOM
// and from `window`.
//
// Why separate: the browser's speechSynthesis does not exist in the node
// environment the project's vitest runs in, and the project has no jsdom. Put
// the choosing here and it is testable; leave it inline in the page entry and
// the only way to check "does the slow pass actually run slower" is to hold a
// phone. app/javascript/pages/speak-topic.js does the wiring.

// The browser default. Naming it keeps the two speeds comparable in one place.
export const NORMAL_RATE = 1;

// ⚠️ Slower than this and the synthesiser starts inserting pauses inside a
// word, which teaches the wrong rhythm. This is the floor that still sounds
// like a sentence.
export const SLOW_RATE = 0.6;

// A BCP 47 tag for English is "en", or "en" followed by a subtag separator.
// ⛔ Not a prefix match on "en": "eng-XX" and "enz" are other languages.
const ENGLISH_TAG = /^en(?:[-_]|$)/i;

/**
 * The English voices among whatever the browser reported.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices] the browser's voice list
 * @returns {Array<SpeechSynthesisVoice>} the English ones, in the reported order
 */
export function englishVoices(voices) {
  if (!Array.isArray(voices)) return [];

  return voices.filter((voice) => typeof voice?.lang === 'string' && ENGLISH_TAG.test(voice.lang));
}

/**
 * Whether the playback controls have earned the right to be visible.
 *
 * A control that is visible and does nothing is worse than no control: this
 * reader already believes the problem is them. So this answers false for a
 * browser with no speech synthesis, for a device with no English voice, and
 * for the empty list iOS returns on the first call — and the page asks again
 * when the browser says the list has changed.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices] the browser's voice list, or nothing
 * @returns {boolean} true when the device can actually speak an English line
 */
export function shouldRevealControls(voices) {
  return englishVoices(voices).length > 0;
}

/**
 * One voice per side of the counter.
 *
 * The dialogue is a scene between two people and the learner practises both
 * parts, so hearing two voices is the cue that says which half is theirs.
 * Where the device has only one English voice both roles share it — ⛔ never
 * disable a role for this, the reader still needs to hear the line.
 *
 * @param {Array<SpeechSynthesisVoice>} [voices] the browser's voice list
 * @returns {{other: ?SpeechSynthesisVoice, learner: ?SpeechSynthesisVoice}}
 */
export function assignRoleVoices(voices) {
  const english = englishVoices(voices);
  if (english.length === 0) return { other: null, learner: null };

  // Deterministic by position, so the same device gives the same role the same
  // voice on every page and a "line 4 sounds wrong" report stays reproducible.
  return { other: english[0], learner: english[1] || english[0] };
}

/**
 * The utterance rate for a named speed.
 *
 * @param {string} [speed] "normal" or "slow"
 * @returns {number} the rate to hand to the utterance
 */
export function rateFor(speed) {
  return speed === 'slow' ? SLOW_RATE : NORMAL_RATE;
}

/**
 * The lines of a dialogue in the order they are spoken.
 *
 * Order comes off the line numbers, not off the order the elements sit in, and
 * a line with no usable number is dropped rather than spoken out of place.
 *
 * @param {Array<{number: number}>} [lines] the lines, in any order
 * @returns {Array<{number: number}>} a new array, ordered by line number
 */
export function playbackOrder(lines) {
  if (!Array.isArray(lines)) return [];

  return lines
    .filter((line) => Number.isFinite(line?.number))
    .sort((a, b) => a.number - b.number);
}
